import React, { useState, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CssBaseline, Container, ThemeProvider, PaletteMode, Box } from '@mui/material';
import LandingPage from './pages/LandingPage';
import { Predictions } from './pages/Predictions';
import RecentRaces from './pages/RecentRaces';
import Profile from './pages/Profile';
import { Matchups } from './pages/Matchups';
import { Legal } from './pages/Legal';
import { Simulator } from './pages/Simulator';
import { LiveRace } from './pages/LiveRace';
import { TrackSimilarity } from './pages/TrackSimilarity';
import { Research } from './pages/Research';
// The admin page is a view only (the backend's read:admin check guards the data), so it's bundled normally: splitting
// it into its own chunk made the main bundle 13.6 kB bigger, more than the page itself (5.8 kB).
import { Admin } from './pages/Admin';
import Navbar from './components/Navbar';
import TrackRankings from './components/TrackRankings';
import ProtectedRoute from './components/ProtectedRoute';
import { getTheme } from './theme';
import { Footer } from './components/Footer';
import { track } from './utils/track';

const queryClient = new QueryClient();

export type ColorModeContextType = {
  toggleColorMode: () => void;
  mode: PaletteMode;
};

export const ColorModeContext = React.createContext<ColorModeContextType>({
  toggleColorMode: () => {},
  mode: 'dark',
});

function App() {
  const [mode, setMode] = useState<PaletteMode>('dark');

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
      mode,
    }),
    [mode]
  );

  const theme = useMemo(() => getTheme(mode), [mode]);

  return (
    <QueryClientProvider client={queryClient}>
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <PageViews />
            <AppLayout />
          </Router>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </QueryClientProvider>
  );
}

/** A usage event per page visited (see utils/track.ts). */
function PageViews() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    track('page_view', pathname);
  }, [pathname]);
  return null;
}

function AppLayout() {
  const routes = (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/terms" element={<Legal doc="terms" />} />
      <Route path="/privacy" element={<Legal doc="privacy" />} />
      {/* Old link: the Predictions page is now Loop Data. */}
      <Route
        path="/predictions"
        element={
          <ProtectedRoute>
            <Predictions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/loop-data"
        element={
          <ProtectedRoute>
            <Predictions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recent-races"
        element={
          <ProtectedRoute>
            <RecentRaces />
          </ProtectedRoute>
        }
      />
      <Route
        path="/track-rankings"
        element={
          <ProtectedRoute>
            <TrackRankings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/matchups"
        element={
          <ProtectedRoute>
            <Matchups />
          </ProtectedRoute>
        }
      />
      <Route
        path="/research"
        element={
          <ProtectedRoute>
            <Research />
          </ProtectedRoute>
        }
      />
      <Route
        path="/research/:slug"
        element={
          <ProtectedRoute>
            <Research />
          </ProtectedRoute>
        }
      />
      <Route
        path="/track-similarity"
        element={
          <ProtectedRoute>
            <TrackSimilarity />
          </ProtectedRoute>
        }
      />
      <Route
        path="/simulator"
        element={
          <ProtectedRoute>
            <Simulator />
          </ProtectedRoute>
        }
      />
      <Route
        path="/live"
        element={
          <ProtectedRoute>
            <LiveRace />
          </ProtectedRoute>
        }
      />
      {/* AI chat is switched off for now (backend: AI_CHAT_ENABLED); old links go to the Simulator. */}
      <Route path="/ai-chat" element={<Navigate to="/simulator" replace />} />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />
    </Routes>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar />
      <Container maxWidth="lg" sx={{ flexGrow: 1 }}>
        {routes}
      </Container>
      <Footer />
    </Box>
  );
}

export default App; 